import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Clock, CheckCircle, ChevronRight, Truck, Search, Filter, FileText, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const ReadyToDispatch = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Show items waiting for DC, with Accounts, in QC, or already dispatched/in transit for audit
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "in", ["packed_ready", "invoice_pending", "invoice_ready", "dc_confirmed", "qc_passed", "dispatched", "shipped", "partially_shipped", "in_transit", "delivered"]),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const mocList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching DC list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => 
      moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moc.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mocs, searchTerm]);

  const formatDate = (dateString) => {
      if (!dateString) return '---';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'packed_ready':
            return <div style={{fontSize: '11px', color: '#6366f1', fontWeight:'bold'}}>DC PENDING 📋</div>;
        case 'invoice_pending':
            return <div style={{fontSize: '11px', color: '#ef4444', fontWeight:'bold'}}>INVOICE PENDING ⏳</div>;
        case 'invoice_ready':
        case 'dc_confirmed':
        case 'qc_pending':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>INVOICE READY ✅</div>;
        case 'qc_passed':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>QC PASSED ✅</div>;
        case 'dispatched':
        case 'shipped':
            return <div style={{fontSize: '11px', color: '#059669', fontWeight:'bold'}}>DISPATCHED ✅</div>;
        case 'partially_shipped':
            return <div style={{fontSize: '11px', color: '#4338ca', fontWeight:'bold'}}>PARTIALLY SHIPPED 🚚</div>;
        case 'in_transit':
            return <div style={{fontSize: '11px', color: '#f97316', fontWeight:'bold'}}>IN TRANSIT 🚚</div>;
        case 'delivered':
            return <div style={{fontSize: '11px', color: '#059669', fontWeight:'bold'}}>DELIVERED 🎉</div>;
        default:
            return <div style={{fontSize: '11px', color: '#64748b', fontWeight:'bold'}}>{status?.replace(/_/g, ' ').toUpperCase()}</div>;
    }
  };

  const handleRowClick = (moc) => {
    navigate(`/ops/dispatch/${moc.id}`);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Ready to Dispatch (DC Queue)</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Generate Delivery Challans and upload dispatch documents.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Customer, MOC No, or Order No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
      </div>

      {mocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <FileText size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No material waiting for DC!</h3>
          <p style={{ color: '#64748b' }}>Items appear here once packing is complete.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Packed Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Customer</th>
                <th style={thStyle}>Order No.</th>
                <th style={thStyle}>MOC No.</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => (
                <tr 
                  key={moc.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => handleRowClick(moc)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>{formatDate(moc.updatedAt?.toDate ? moc.updatedAt.toDate() : moc.updatedAt)}</td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{moc.customerName}</div>
                  </td>
                  <td style={tdStyle}>{moc.orderNumber || '---'}</td>
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold', fontSize: '13px'}}>{moc.mocNo}</div>
                  </td>
                  <td style={tdStyle}>{moc.warehouse}</td>
                  <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      {moc.dispatchStatus === 'packed_ready' ? 'Prepare DC' : 'View/Edit DC'} <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

const filterBarStyle = { display: 'flex', gap: '15px', marginBottom: '20px' };
const searchInputStyle = { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

export default ReadyToDispatch;
